// @frame/sdk/shared — helpers used on both sides of a module (server.ts and client.tsx).
// Both @frame/sdk/server and @frame/sdk/tv re-export everything here.
import { DEFAULT_LOCALE, LOCALES, LOCALE_NAMES, LOCALE_TAGS, isLocale, localize, type Locale, type LocalizedText } from '@frame/protocol';

export { DEFAULT_LOCALE, LOCALES, LOCALE_NAMES, LOCALE_TAGS, isLocale, localize };
export type { Locale, LocalizedText };

/** A message is a string or a function building one (plurals, interpolation). */
export type Message = string | ((...args: any[]) => string);
export type Messages = Record<string, Message>;

export interface MessageCatalog<M extends Messages> {
  /** Messages for a language; keys missing in that language fall back to English. */
  get(locale: Locale): M;
}

/**
 * A module's UI strings, one set per language. English is required and defines the keys;
 * other languages may omit keys (they fall back to English).
 *
 *   const messages = defineMessages({
 *     en: { title: 'Lights', count: (n: number) => (n === 1 ? '1 light' : `${n} lights`) },
 *     tr: { title: 'Işıklar', count: (n: number) => `${n} lamba` },
 *   });
 *   // TV:     const t = useT(messages); t.title; t.count(3)
 *   // server: const t = messages.get(ctx.locale);
 */
export function defineMessages<M extends Messages>(catalog: { en: M } & { [L in Exclude<Locale, 'en'>]?: Partial<NoInfer<M>> }): MessageCatalog<M> {
  const cache = new Map<Locale, M>();
  return {
    get(locale) {
      let m = cache.get(locale);
      if (!m) {
        const own = (catalog as Partial<Record<Locale, Partial<M>>>)[locale];
        m = own && locale !== 'en' ? { ...catalog.en, ...own } : catalog.en;
        cache.set(locale, m);
      }
      return m;
    },
  };
}
